// Landing page for the public marketing site. This component is intentionally
// heavy on layout/CTA copy and light on logic; the only interactivity is the
// navigation to generator and auth flows.
import { Shirt, Sparkles, ArrowRight, Sword, Heart, Crown, Users, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Featured3DGallerySection from './components/Featured3DGallerySection';
import CommunityGallerySection from './components/CommunityGallerySection';
import { ErrorBoundaryTest } from './components/ErrorBoundaryTest';

function App() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-orange-50/30 to-amber-50/30">
      {/* Development-only Error Boundary test button */}
      <ErrorBoundaryTest />

      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered 3D Asset Creation & Marketplace
            </div>
            <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Skip the 3D Skills. Keep 80-90% of Sales.
            </h1>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed">
              AI turns your ideas into production-ready 3D assets. List on our marketplace and earn from every sale. No modeling experience needed. No expensive software. Just describe what you want, we build it, you profit.
            </p>
            <div className="flex flex-col items-start gap-4">
              <button
                onClick={() => navigate('/auth?mode=signup&tier=free')}
                className="px-10 py-5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all font-bold text-xl shadow-lg shadow-orange-600/20 flex items-center gap-2"
              >
                Get 10 Free Generations Now
                <ArrowRight className="w-6 h-6" />
              </button>
              <p className="text-sm text-gray-500">No credit card • Start earning in minutes</p>
            </div>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 p-2 border border-orange-100">
            <Featured3DGallerySection compact={true} />
          </div>
        </div>
      </section>

      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Pick Your Money-Maker</h2>
          <p className="text-lg text-gray-600">Each asset type = a new revenue stream. Build your portfolio in minutes, not months.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="relative bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 rounded-2xl p-6 border-2 border-orange-200 hover:border-orange-300 hover:shadow-2xl hover:scale-[1.02] transition-all group shadow-lg min-h-[420px] flex flex-col">
            <div className="absolute top-3 right-3 px-3 py-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold rounded-full shadow-lg">
              PREMIUM
            </div>
            <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Pet Generator</h3>
            <p className="text-gray-700 leading-relaxed mb-4 text-sm">
              Generate custom 3D pets with full rigging, animations, and personality. Upload artwork or describe your vision for instant creation.
            </p>
            <ul className="space-y-1.5 text-xs text-gray-700 mb-4 flex-grow">
              <li className="flex items-start gap-2">
                <span className="text-orange-600 font-bold">•</span>
                <span>Full rigging & animations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 font-bold">•</span>
                <span>Upload custom artwork</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 font-bold">•</span>
                <span>Multiple art styles</span>
              </li>
            </ul>
            <button
              onClick={() => navigate('/generate/pet')}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl hover:from-orange-700 hover:to-amber-700 transition-all font-semibold text-sm shadow-lg shadow-orange-600/30 flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Start with Pets
            </button>
          </div>

          <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-green-200 hover:shadow-xl hover:scale-[1.02] transition-all group min-h-[380px] flex flex-col">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Sword className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Accessory Generator</h3>
            <p className="text-gray-600 leading-relaxed mb-4 text-sm">
              Create weapons, tools, handhelds, hats, and props. AI handles modeling and optimization for instant marketplace readiness.
            </p>
            <ul className="space-y-1.5 text-xs text-gray-600 mb-4 flex-grow">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">•</span>
                <span>Auto rigging & physics</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">•</span>
                <span>Multiple categories</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">•</span>
                <span>LOD generation</span>
              </li>
            </ul>
            <button
              onClick={() => navigate('/generate/accessory')}
              className="w-full px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold text-sm shadow-lg shadow-green-600/20 flex items-center justify-center gap-2"
            >
              Generate Accessories
            </button>
          </div>

          <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl hover:scale-[1.02] transition-all group min-h-[380px] flex flex-col">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Shirt className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Clothing Generator</h3>
            <p className="text-gray-600 leading-relaxed mb-4 text-sm">
              Design shirts, pants, textures, and templates with AI-powered pattern generation. Professional results in seconds.
            </p>
            <ul className="space-y-1.5 text-xs text-gray-600 mb-4 flex-grow">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Custom patterns & textures</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Auto-fit to all body types</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Export-ready instantly</span>
              </li>
            </ul>
            <button
              onClick={() => navigate('/generate/clothing')}
              className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold text-sm shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
            >
              Generate Clothing
            </button>
          </div>

          <div className="relative bg-gray-50 rounded-2xl p-6 border-2 border-gray-200 min-h-[380px] flex flex-col opacity-75">
            <div className="absolute top-3 right-3 px-3 py-1 bg-gray-400 text-white text-xs font-bold rounded-full">
              COMING SOON
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-gray-300 to-gray-400 rounded-xl flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">NPC Generator</h3>
            <p className="text-gray-500 leading-relaxed mb-4 text-sm">
              Generate complete NPC characters with unique appearances, animations, and AI-driven behaviors.
            </p>
            <ul className="space-y-1.5 text-xs text-gray-500 mb-4 flex-grow">
              <li className="flex items-start gap-2">
                <span className="text-gray-400 font-bold">•</span>
                <span>Procedural generation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 font-bold">•</span>
                <span>Custom animation sets</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 font-bold">•</span>
                <span>Dialogue & behaviors</span>
              </li>
            </ul>
            <button
              disabled
              className="w-full px-4 py-2.5 bg-gray-300 text-gray-500 rounded-xl font-semibold text-sm cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Lock className="w-4 h-4" />
              Coming Soon
            </button>
          </div>
        </div>
      </section>

      {/* Community Gallery Section */}
      <CommunityGallerySection />

      <section className="bg-gradient-to-br from-orange-600 to-amber-600 text-white py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Crown className="w-16 h-16 mx-auto mb-6 text-amber-200" />
          <h2 className="text-4xl font-bold mb-6">Ready to Start Creating?</h2>
          <p className="text-xl text-orange-100 mb-10">
            Join thousands of UGC creators using ForgeBlocks to generate high-value Roblox assets in seconds.
          </p>
          <button
            onClick={() => navigate('/select-generator')}
            className="px-8 py-4 bg-white text-orange-600 rounded-xl hover:bg-gray-50 transition-all font-semibold text-lg shadow-xl flex items-center gap-2 mx-auto"
          >
            Get Started Now
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <footer className="border-t border-gray-100 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center justify-center gap-8 text-sm text-gray-600">
            <a href="#about" className="hover:text-gray-900 transition-colors">About</a>
            <button onClick={() => navigate('/pricing')} className="hover:text-gray-900 transition-colors">Pricing</button>
            <a href="#api" className="hover:text-gray-900 transition-colors">API</a>
            <a href="#support" className="hover:text-gray-900 transition-colors">Support</a>
            <a href="/privacy" className="hover:text-gray-900 transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-gray-900 transition-colors">Terms</a>
          </div>
          <div className="pt-6 text-center text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} ForgeBlocks. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
